import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Selo, type CorSelo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Formulario, Selecao, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { del, mensagemDeErro, patch, post } from '../../nucleo/api';
import { data } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { invalidarEstoque, qtd, rotuloProduto } from './comum';
import type { Lote, SituacaoLote } from './tipos';

const SITUACOES: SituacaoLote[] = ['vencido', 'vencendo', 'ok', 'sem_validade'];
const COR: Record<SituacaoLote, CorSelo> = { vencido: 'perigo', vencendo: 'laranja', ok: 'verde', sem_validade: 'neutro' };
const COR_TEXTO: Record<SituacaoLote, string> = { vencido: 'text-perigo', vencendo: 'text-laranja', ok: '', sem_validade: '' };

/** M9.6: lotes e validade. Vencidos e os que vencem em 30 dias ganham destaque. */
export function Lotes() {
  const { t } = useTranslation('estoque');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const [situacao, setSituacao] = useState('');
  const [comSaldo, setComSaldo] = useState(true);
  const [aberto, setAberto] = useState<Lote | 'novo' | null>(null);

  const colunas: Coluna<Lote>[] = [
    { chave: 'codigo', rotulo: t('campos.lote'), mono: true, principal: true, ordenavel: true, render: (l) => <span className="font-medium">{l.codigo}</span> },
    {
      chave: 'produto_nome',
      rotulo: t('campos.produto'),
      render: (l) => (
        <span className="flex min-w-0 flex-col">
          <span className="truncate" title={l.produto_nome}>{l.produto_nome}</span>
          <span className="numero truncate text-xs text-tinta-3" title={l.produto_codigo}>{l.produto_codigo}</span>
        </span>
      ),
    },
    {
      chave: 'validade',
      rotulo: t('campos.validade'),
      mono: true,
      ordenavel: true,
      render: (l) => (
        <span className={`${COR_TEXTO[l.situacao]} ${l.situacao === 'vencido' || l.situacao === 'vencendo' ? 'font-semibold' : ''}`}>
          {l.validade ? data(l.validade) : ''}
          {l.dias_para_vencer !== null && (l.situacao === 'vencido' || l.situacao === 'vencendo') ? ` (${l.dias_para_vencer < 0 ? t('lote.haDias', { count: Math.abs(l.dias_para_vencer) }) : t('lote.emDias', { count: l.dias_para_vencer })})` : ''}
        </span>
      ),
    },
    { chave: 'fabricacao', rotulo: t('campos.fabricacao'), mono: true, oculta: true, render: (l) => (l.fabricacao ? data(l.fabricacao) : '') },
    { chave: 'saldo', rotulo: t('campos.saldo'), alinhar: 'direita', mono: true, render: (l) => `${qtd(l.saldo)} ${l.unidade}` },
    { chave: 'situacao', rotulo: t('campos.situacao'), render: (l) => <Selo cor={COR[l.situacao]}>{t(`lote.situacao.${l.situacao}`)}</Selo> },
  ];

  return (
    <>
      {s.pode('estoque.criar') && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('novo')}>
            {t('acoes.novoLote')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Tabela<Lote>
        tela="estoque.lotes"
        caminho="/estoque/lotes"
        params={{ situacao: situacao || undefined, com_saldo: comSaldo ? true : undefined }}
        filtros={{ situacao, com_saldo: comSaldo }}
        aoAplicarFiltros={(f) => {
          setSituacao(String(f.situacao));
          setComSaldo(f.com_saldo === true);
        }}
        colunas={colunas}
        aoClicar={(l) => setAberto(l)}
        vazio={<Vazio texto={t('vazio.lotes')} />}
        filtrosExtra={
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-full sm:w-56">
              <Selecao aria-label={t('campos.situacao')} vazio={tn('comum.todos')} opcoes={SITUACOES.map((x) => ({ valor: x, rotulo: t(`lote.situacao.${x}`) }))} value={situacao} onChange={(e) => setSituacao(e.target.value)} />
            </div>
            <label className="flex items-center gap-2 text-sm text-tinta-2">
              <input type="checkbox" checked={comSaldo} onChange={(e) => setComSaldo(e.target.checked)} />
              {t('lote.soComSaldo')}
            </label>
          </div>
        }
      />
      <DrawerLote aberto={aberto} aoFechar={() => setAberto(null)} />
    </>
  );
}

function DrawerLote({ aberto, aoFechar }: { aberto: Lote | 'novo' | null; aoFechar: () => void }) {
  const { t } = useTranslation('estoque');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [apagar, setApagar] = useState(false);
  const atual = aberto && aberto !== 'novo' ? aberto : null;

  const campos: DefCampo[] = [
    ...(atual
      ? []
      : [{ nome: 'produto_id', rotulo: t('campos.produto'), tipo: 'busca' as const, obrigatorio: true, busca: { caminho: '/estoque/produtos', rotulo: rotuloProduto } }]),
    { nome: 'codigo', rotulo: t('campos.codigoLote'), tipo: 'texto', obrigatorio: true },
    { nome: 'fabricacao', rotulo: t('campos.fabricacao'), tipo: 'data', largura: 'meia' },
    { nome: 'validade', rotulo: t('campos.validade'), tipo: 'data', largura: 'meia' },
  ];

  const remover = useMutation({
    mutationFn: () => del(`/estoque/lotes/${atual!.id}`),
    onSuccess: async () => {
      await invalidarEstoque(qc);
      setApagar(false);
      aoFechar();
    },
    onError: (e) => {
      setApagar(false);
      toast.erro(mensagemDeErro(e));
    },
  });

  return (
    <>
      <Drawer
        aberto={!!aberto}
        aoFechar={aoFechar}
        titulo={atual ? atual.codigo : t('acoes.novoLote')}
        rodape={
          <>
            {atual && s.pode('estoque.excluir') && (
              <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setApagar(true)} className="mr-auto">
                {tn('comum.excluir')}
              </Botao>
            )}
            {(atual ? s.pode('estoque.editar') : s.pode('estoque.criar')) && (
              <>
                <Botao variante="fantasma" onClick={aoFechar}>
                  {tn('comum.cancelar')}
                </Botao>
                <Botao variante="primario" type="submit" form="form-lote">
                  {tn('comum.salvar')}
                </Botao>
              </>
            )}
          </>
        }
      >
        {atual && (
          <p className="mb-4 text-sm text-tinta-3">
            {atual.produto_codigo} {atual.produto_nome}
          </p>
        )}
        <Formulario
          key={atual?.id ?? 'novo'}
          id="form-lote"
          campos={campos}
          inicial={atual ? { ...atual } : {}}
          aoEnviar={async (v) => {
            const corpo = { codigo: v.codigo, validade: v.validade || null, fabricacao: v.fabricacao || null };
            if (atual) await patch(`/estoque/lotes/${atual.id}`, corpo);
            else await post('/estoque/lotes', { ...corpo, produto_id: v.produto_id });
            await invalidarEstoque(qc);
            toast.sucesso(tn('comum.salvo'));
            aoFechar();
          }}
        />
      </Drawer>
      <Confirmar aberto={apagar} aoFechar={() => setApagar(false)} aoConfirmar={() => remover.mutate()} carregando={remover.isPending} perigoso titulo={t('confirmar.excluirLote')} texto={t('confirmar.excluirLoteTexto')} />
    </>
  );
}
